<template>
  <div class="pageRoot linkListPage collectionModule">
    <div class="pageShell" ref="pageCard">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <classify-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="分类名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="封面图"
                    align="center"
                    header-align="center"
                    width="88"
                  >
                    <template slot-scope="scope">
                      <div class="previewCell">
                        <el-image
                          v-if="scope.row.icon"
                          class="previewCell__img classifyPreview"
                          :src="scope.row.icon"
                          :preview-src-list="[scope.row.icon]"
                          fit="cover"
                        />
                        <span v-else class="previewCell__empty">—</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="分类类型"
                    prop="type"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getClassifyTypeVariant(scope.row.type)"
                      >
                        {{ scope.row.type || '—' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="状态"
                    align="center"
                    header-align="center"
                    width="100"
                  >
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="
                          scope.row.status === 1
                            ? 'statusPill--online'
                            : 'statusPill--offline'
                        "
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ scope.row.status === 1 ? '上线' : '下线' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="已关联页面"
                    align="center"
                    header-align="center"
                    min-width="180"
                    class-name="categoryColumn"
                  >
                    <template slot-scope="scope">
                      <div
                        v-if="
                          scope.row.pageSimpleVOSet &&
                          scope.row.pageSimpleVOSet.length
                        "
                        class="categoryCell"
                      >
                        <router-link
                          v-for="item in getVisiblePages(scope.row.pageSimpleVOSet)"
                          :key="item.id"
                          :to="{ path: '/collection/page/info/' + item.id }"
                          class="categoryCell__link"
                        >
                          <el-tag
                            size="mini"
                            effect="plain"
                            class="categoryTag pageTag"
                            :class="'pageTag--tone-' + getPageTagTone(item.id)"
                          >
                            {{ item.name }}
                          </el-tag>
                        </router-link>
                        <el-tag
                          v-if="getHiddenPageCount(scope.row.pageSimpleVOSet) > 0"
                          size="mini"
                          effect="plain"
                          class="categoryTag"
                        >
                          +{{ getHiddenPageCount(scope.row.pageSimpleVOSet) }}
                        </el-tag>
                      </div>
                      <span v-else class="cellEmptyPill">未关联</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="备注"
                    prop="remark"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span v-if="scope.row.remark" class="cellMuted">{{
                        scope.row.remark
                      }}</span>
                      <span v-else class="cellEmptyPill">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="320"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-document-copy"
                          @click.stop="copyItem(scope.row.id)"
                        >
                          复制
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="goInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--emphasis"
                          icon="el-icon-tickets"
                          @click.stop="
                            goResources(scope.row.id, scope.row.name, scope.row.type)
                          "
                        >
                          资源列表
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                          icon="el-icon-delete"
                          v-check-role
                          @click.stop="deleteCategoryRow(scope.row.id)"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-folder-opened" /></div>
                <p class="tableEmpty__title">暂无分类</p>
                <p class="tableEmpty__desc">调整筛选条件或新增分类。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="$router.push('/collection/classify/create')"
                >
                  新增分类
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <classify-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mutations } from '../../../utils/store'
import { getCategoryList, copyCategory, deleteCategory } from '../../../api/classifyManage'
import { getClassifyResourceType } from '../../../api/comment'
import { getAllPage } from '../../../api/dataManage'
import ClassifyListFilterPanel from './ClassifyListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import {
  getClassifyTypeVariant,
  getHiddenPageCount,
  getPageTagTone,
  getVisiblePages,
} from './classifyListHelpers'

export default {
  name: 'ClassifyList',
  components: {
    ClassifyListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      name: '',
      type: '',
      pageId: '',
      remark: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 10,
      total: 0,
      loading: false,
      typeOptions: [],
      pageOptions: [],
      /** mixin 兼容：分类列表无跨页勾选 */
      multipleSelection: [],
      categoryOptions: [],
      dataOptions: [],
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.name, this.type, this.pageId, this.remark].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.name || '').trim()) {
        chips.push({ key: 'name', label: '分类名称', value: this.name.trim() })
      }
      if (this.type !== '' && this.type != null) {
        chips.push({
          key: 'type',
          label: '分类类型',
          value: this.resolveTypeLabel(this.type),
        })
      }
      if (this.pageId !== '' && this.pageId != null) {
        chips.push({
          key: 'pageId',
          label: '页面',
          value: this.resolvePageLabel(this.pageId),
        })
      }
      if (String(this.remark || '').trim()) {
        chips.push({ key: 'remark', label: '备注', value: this.remark.trim() })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        name: this.name,
        type: this.type,
        pageId: this.pageId,
        remark: this.remark,
        activeFilterCount: this.activeFilterCount,
        typeOptions: this.typeOptions,
        pageOptions: this.pageOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/collection/classify/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:name': (value) => {
          this.name = value
        },
        'update:type': (value) => {
          this.type = value
        },
        'update:pageId': (value) => {
          this.pageId = value
        },
        'update:remark': (value) => {
          this.remark = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getClassify()
    this.getAllPages()
    this.getList()
  },
  methods: {
    getClassifyTypeVariant,
    getPageTagTone,
    getVisiblePages,
    getHiddenPageCount,
    getAllPages() {
      getAllPage({ type: '主信息流' }).then((res) => {
        if (res.success) {
          this.pageOptions = res.data || []
        }
      })
    },
    getList() {
      this.loading = true
      getCategoryList({
        name: this.name,
        remark: this.remark,
        type: this.type,
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        pageId: this.pageId,
      })
        .then((res) => {
          if (res.success) {
            this.tableData = res.data || []
            this.total = res.total
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.name = ''
      this.type = ''
      this.pageId = ''
      this.remark = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    resolveTypeLabel(typeValue) {
      const match = (this.typeOptions || []).find(
        (item) => String(item.categoryType) === String(typeValue)
      )
      return (match && match.name) || String(typeValue)
    },
    resolvePageLabel(pageId) {
      const match = (this.pageOptions || []).find(
        (item) => String(item.id) === String(pageId)
      )
      return (match && match.name) || String(pageId)
    },
    setBreadTitle: mutations.setBreadTitle,
    goResources(id, name, type) {
      this.$router.push(
        `/collection/classify/resources/${id}?name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}`
      )
      this.setBreadTitle(`${name}-列表`)
    },
    goInfo(id) {
      const row = (this.tableData || []).find(
        (item) => String(item.id) === String(id)
      )
      if (row && row.name) {
        this.setBreadTitle(`${row.name}-查看`)
      }
      this.$router.push(`/collection/classify/info/${id}`)
    },
    copyItem(id) {
      this.$confirm('此操作将复制该分类，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => copyCategory(id))
        .then((res) => {
          if (res.success) {
            this.$message.success('复制成功')
            this.getList()
          }
        })
        .catch(() => {})
    },
    deleteCategoryRow(id) {
      this.$confirm('此操作将永久删除该分类，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => deleteCategory({ id }))
        .then((res) => {
          if (res.success) {
            this.$message.success('删除成功')
            this.getList()
          }
        })
        .catch(() => {})
    },
    getClassify() {
      getClassifyResourceType().then((res) => {
        const payload = res.data
        if (payload && payload.success) {
          this.typeOptions = payload.data || []
        }
      })
    },
  },
}
</script>

<style scoped>
.collectionModule .classifyPreview {
  width: 48px;
  height: 48px;
  border-radius: 8px;
}

.categoryCell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.pageTag--tone-0 {
  --page-tag-bg: rgba(224, 242, 254, 0.85);
  --page-tag-ink: #0369a1;
}
.pageTag--tone-1 {
  --page-tag-bg: rgba(237, 233, 254, 0.9);
  --page-tag-ink: #6d28d9;
}
.pageTag--tone-2 {
  --page-tag-bg: rgba(254, 243, 199, 0.9);
  --page-tag-ink: #b45309;
}
.pageTag--tone-3 {
  --page-tag-bg: rgba(204, 251, 241, 0.85);
  --page-tag-ink: #0f766e;
}
.pageTag--tone-4 {
  --page-tag-bg: rgba(255, 228, 230, 0.9);
  --page-tag-ink: #be123c;
}
.pageTag--tone-5 {
  --page-tag-bg: rgba(243, 244, 246, 0.95);
  --page-tag-ink: #57534e;
}

.pageTag.categoryTag {
  background: var(--page-tag-bg) !important;
  border-color: transparent !important;
  color: var(--page-tag-ink) !important;
}

.rowActionBtn--emphasis {
  color: var(--link-accent) !important;
  font-weight: var(--link-font-semibold);
}

.cellMuted {
  color: var(--link-ink-secondary);
}

::v-deep .el-pagination__jump {
  margin-left: -5px;
}
</style>
